import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'horizontal' | 'ranking';
  rank?: number;
}

export default function ComicCard({ comic, variant = 'default', rank }: ComicCardProps) {
  if (variant === 'ranking') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="flex items-center gap-4 group p-3 rounded-lg hover:bg-background-100/70 transition-colors cursor-pointer"
      >
        <span className={`text-2xl font-bold font-heading w-8 text-center flex-shrink-0 ${
          rank === 1 ? 'text-accent-500' : rank === 2 ? 'text-accent-400' : rank === 3 ? 'text-accent-300' : 'text-foreground-400'
        }`}>
          {rank}
        </span>
        <div className="w-14 h-18 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-accent-500 font-medium">★ {comic.rating}</span>
            <span className="text-xs text-foreground-500">{Math.floor(comic.views / 10000)}万</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="flex gap-4 group p-3 rounded-lg hover:bg-background-100/70 transition-colors cursor-pointer"
      >
        <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
          <div>
            <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.categoryName}</p>
            <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
              {comic.description}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-accent-500 font-medium">★ {comic.rating}</span>
            <span className="text-xs text-foreground-500">{Math.floor(comic.views / 10000)}万人阅读</span>
            {comic.isCompleted && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">已完结</span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        {comic.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500 text-white text-xs font-medium">
            热门
          </span>
        )}
        {comic.isNew && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium">
            新作
          </span>
        )}
        {comic.isCompleted && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-foreground-800/70 text-white text-xs font-medium">
            完结
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/90 font-medium">★ {comic.rating}</span>
            <span className="text-xs text-white/70">{Math.floor(comic.views / 10000)}万</span>
          </div>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
        {comic.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
    </Link>
  );
}